import React from 'react';
import styled from 'styled-components';
import { BillHeader, ContainerLogo, DivFactura } from '../../../ui';
import { DownloadableTable } from '../../../ui/styles/DownloadableTable';
import { PdfWrapper } from '../../../order/ui/DownloadableOrderRecap';
import { useTenantContext } from '../../../tenant/TenantContext';
import { groupPackLines, packParentName } from '../../domain/packGrouping';
import {
  DIAS_VALIDEZ_PRESUPUESTO,
  lineBasePrice,
  lineDiscountPercent,
  lineNetAmount,
  lineNetPrice,
  lineTaxes,
  quoteReference,
  quoteTotals,
  quoteValidUntil
} from '../../domain/quoteSummary';

const currencyFmt = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const percentFmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtDate = (date) => date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

/* La tabla comun reparte 7 columnas (y suma mas del 100 %); el presupuesto
   tiene 8 y con table-layout fixed la ultima (Importe) se salia de la hoja. */
const QuoteTable = styled(DownloadableTable)`
  font-size: 13px;

  th:nth-child(1), td:nth-child(1) { width: 12%; }
  th:nth-child(2), td:nth-child(2) { width: 32%; }
  th:nth-child(3), td:nth-child(3) { width: 6%; }
  th:nth-child(4), td:nth-child(4) { width: 11%; }
  th:nth-child(5), td:nth-child(5) { width: 8%; }
  th:nth-child(6), td:nth-child(6) { width: 11%; }
  th:nth-child(7), td:nth-child(7) { width: 7%; }
  th:nth-child(8), td:nth-child(8) { width: 13%; }
`;

const QuoteTitle = styled.div`
  text-align: right;
  font-family: 'Arial', sans-serif;

  .title { font-size: 26px; font-weight: 700; letter-spacing: 1px; }
  .meta { font-size: 13px; color: #444; line-height: 1.5; }
`;

const Party = styled.div`
  font-family: 'Arial', sans-serif;
  font-size: 13px;
  line-height: 1.5;

  .label { font-size: 11px; text-transform: uppercase; color: #777; letter-spacing: 0.5px; }
  .name { font-weight: 700; font-size: 14px; }
`;

const Totals = styled.table`
  margin-left: auto;
  margin-top: 16px;
  font-family: 'Arial', sans-serif;
  font-size: 13px;
  border-collapse: collapse;

  td { padding: 4px 10px; text-align: right; white-space: nowrap; }
  td.label { color: #555; }
  tr.grand td { font-size: 16px; font-weight: 700; border-top: 2px solid #333; padding-top: 8px; }
`;

const Notes = styled.div`
  margin-top: 24px;
  font-family: 'Arial', sans-serif;
  font-size: 11px;
  color: #666;
  line-height: 1.5;
`;

/**
 * Plantilla del presupuesto que el comercial descarga desde el carrito (se pinta
 * oculta y jsPDF la convierte, igual que el resumen del pedido). Los importes son
 * los del carrito del cliente suplantado: precio de tarifa, descuento y neto por
 * línea, y los totales que devuelve el api (los mismos que verá al tramitar).
 */
export const DownloadableQuote = ({ cart, cliente, comercialCode, issuedAt }) => {
  const { logo, businessName, address, tel } = useTenantContext();

  if (!cart || !cliente) {
    return <></>;
  }

  const date = issuedAt ?? new Date();
  const products = cart.products ?? [];
  const lines = groupPackLines(products);
  const totals = quoteTotals(products);
  const conRecargo = totals.re.length > 0;

  return (
    <PdfWrapper>
      <DivFactura>
        <BillHeader className="mb-4">
          <ContainerLogo>
            <img src={logo} alt="logo" />
          </ContainerLogo>
          <QuoteTitle>
            <div className="title">PRESUPUESTO</div>
            <div className="meta">
              <div>Ref. {quoteReference(cliente.numCliente, date)}</div>
              <div>Fecha: {fmtDate(date)}</div>
              <div>Válido hasta: {fmtDate(quoteValidUntil(date))}</div>
            </div>
          </QuoteTitle>
        </BillHeader>

        <div className="d-flex justify-content-between align-items-start my-4 gap-4">
          <Party>
            <div className="label">Emisor</div>
            <div className="name">{businessName}</div>
            <div>{address}</div>
            {tel && <div>Tel. {tel}</div>}
            {comercialCode && <div>Comercial: {comercialCode}</div>}
          </Party>
          <Party style={{ textAlign: 'right' }}>
            <div className="label">Cliente</div>
            <div className="name">{cliente.nombreRazonSocial}</div>
            <div>Nº cliente: {cliente.numCliente}</div>
            {cliente.cifNiff && <div>CIF/NIF: {cliente.cifNiff}</div>}
          </Party>
        </div>

        <QuoteTable>
          <thead>
            <tr>
              <th>Ref.</th>
              <th className="desc">Descripción</th>
              <th className="center">Uds</th>
              <th className="num">P. tarifa</th>
              <th className="num">Dto.</th>
              <th className="num">P. neto</th>
              <th className="num">IVA</th>
              <th className="num">Importe</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const discount = lineDiscountPercent(line);
              return (
                <tr key={`${line.id ?? line.code}-${index}`}>
                  <td style={{ whiteSpace: 'nowrap' }}>{line.code}</td>
                  <td className="desc">
                    {line.packOrigin && (
                      <span style={{ fontWeight: 700 }}>INCLUIDO con {packParentName(products, line) ?? 'el producto anterior'}: </span>
                    )}
                    {line.name ?? 'N/A'}
                  </td>
                  <td className="center">{line.quantity}</td>
                  {line.packOrigin ? (
                    <>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">Incluido</td>
                    </>
                  ) : (
                    <>
                      <td className="num">{currencyFmt.format(lineBasePrice(line))}</td>
                      <td className="num">{discount != null ? `${percentFmt.format(discount)} %` : '—'}</td>
                      <td className="num">{currencyFmt.format(lineNetPrice(line))}</td>
                    </>
                  )}
                  <td className="num">
                    {percentFmt.format(lineTaxes(line).vat)} %
                    {lineTaxes(line).re > 0 && (
                      <div style={{ fontSize: '10px', color: '#666' }}>RE {percentFmt.format(lineTaxes(line).re)} %</div>
                    )}
                  </td>
                  <td className="num">{currencyFmt.format(lineNetAmount(line))}</td>
                </tr>
              );
            })}
          </tbody>
        </QuoteTable>

        <Totals>
          <tbody>
            {totals.discount > 0 && (
              <tr>
                <td className="label">Descuento aplicado sobre tarifa</td>
                <td>{currencyFmt.format(totals.discount)}</td>
              </tr>
            )}
            <tr>
              <td className="label">Base imponible</td>
              <td>{currencyFmt.format(totals.base)}</td>
            </tr>
            {totals.vat.map((row) => (
              <tr key={`iva-${row.rate}`}>
                <td className="label">IVA {percentFmt.format(row.rate)} % sobre {currencyFmt.format(row.base)}</td>
                <td>{currencyFmt.format(row.amount)}</td>
              </tr>
            ))}
            {totals.re.map((row) => (
              <tr key={`re-${row.rate}`}>
                <td className="label">Recargo de equivalencia {percentFmt.format(row.rate)} % sobre {currencyFmt.format(row.base)}</td>
                <td>{currencyFmt.format(row.amount)}</td>
              </tr>
            ))}
            <tr className="grand">
              <td className="label">TOTAL</td>
              <td>{currencyFmt.format(totals.total)}</td>
            </tr>
          </tbody>
        </Totals>

        <Notes>
          <div>Presupuesto válido {DIAS_VALIDEZ_PRESUPUESTO} días desde su fecha de emisión, sujeto a disponibilidad de stock.</div>
          <div>Precios por unidad sin impuestos.{conRecargo ? ' El total incluye el IVA y el recargo de equivalencia del cliente.' : ' El total incluye el IVA.'}</div>
          <div>Este documento no es una factura ni confirma el pedido.</div>
        </Notes>
      </DivFactura>
    </PdfWrapper>
  );
};
